import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Cpu,
  StopCircle,
  Terminal,
  XCircle,
} from "lucide-react";

function fmtMem(kb) {
  if (!kb || kb <= 0) return "";
  if (kb < 1024) return `${kb}KB`;
  return `${(kb / 1024).toFixed(1)}MB`;
}

// Post-run summary view (right panel ‘Output’ tab).
// Live streaming happens in the bottom xterm — this is the final aggregated
// snapshot for review.
export default function OutputConsole({ result, isRunning, errorText }) {
  const ref = useRef(null);
  const [stderrOpen, setStderrOpen] = useState(false);

  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [result, isRunning, errorText]);

  const stdout = result?.stdout || "";
  const stderr = result?.stderr || "";
  const compileError = result?.compile_error;
  const exitCode = result?.exit_code;
  const execTimeMs = result?.exec_time_ms;
  const peakMemKb = result?.peak_memory_kb || 0;
  const timedOut = result?.timed_out;
  const stopped = result?.stopped;

  const empty = !isRunning && !result && !errorText;

  return (
    <div className="h-full flex flex-col bg-ide-panel2" data-testid="output-console">
      <div
        ref={ref}
        className="flex-1 overflow-auto px-3 py-2 font-mono text-[12.5px] leading-5 whitespace-pre-wrap break-words"
      >
        {empty && (
          <div className="text-ide-mute flex items-center gap-2 py-6">
            <Terminal className="h-4 w-4" />
            <span>
              Press <kbd className="px-1 py-0.5 rounded bg-ide-panel border border-ide-border">⌘/Ctrl + Enter</kbd>{" "}
              to run. Live output streams to the terminal below; this tab shows the post-run summary.
            </span>
          </div>
        )}

        {isRunning && (
          <div className="text-sky-300 flex items-center gap-2 py-1">
            <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse-dot" />
            running… (live output in terminal below)
          </div>
        )}

        {errorText && (
          <div className="text-rose-300 flex items-start gap-2 py-1">
            <XCircle className="h-4 w-4 mt-0.5 flex-none" />
            <span>{errorText}</span>
          </div>
        )}

        {compileError && (
          <div className="mb-2 mt-1 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-100 px-2 py-1.5">
            <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-wider mb-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              compile error
            </div>
            <div className="text-amber-100 whitespace-pre-wrap break-words">
              {compileError}
            </div>
          </div>
        )}

        {stdout && (
          <div className="mb-1">
            <div className="text-[11px] uppercase tracking-wider text-ide-mute mb-1">stdout</div>
            <span className="text-ide-text">{stdout}</span>
          </div>
        )}

        {stderr && !compileError && (
          <div className="mt-2">
            <button
              className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-ide-mute hover:text-ide-text transition-colors"
              onClick={() => setStderrOpen((v) => !v)}
            >
              {stderrOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              stderr ({stderr.split("\n").length - 1} lines)
            </button>
            {stderrOpen && (
              <span className="text-rose-300 block mt-1">{stderr}</span>
            )}
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {result && !isRunning && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18 }}
            className="flex items-center gap-3 px-3 py-1.5 border-t border-ide-border text-[11px] text-ide-mute font-mono bg-ide-panel2"
          >
            {exitCode === 0 ? (
              <span className="inline-flex items-center gap-1 text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" />
                exit 0
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-rose-300">
                <XCircle className="h-3.5 w-3.5" />
                exit {exitCode}
              </span>
            )}
            {timedOut && (
              <span className="inline-flex items-center gap-1 text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5" /> timed out
              </span>
            )}
            {stopped && (
              <span className="inline-flex items-center gap-1 text-fuchsia-300">
                <StopCircle className="h-3.5 w-3.5" /> stopped
              </span>
            )}
            <span className="inline-flex items-center gap-1 ml-auto">
              <Clock className="h-3.5 w-3.5" />
              {execTimeMs ?? 0} ms
            </span>
            {peakMemKb > 0 && (
              <span className="inline-flex items-center gap-1" data-testid="output-peak-mem">
                <Cpu className="h-3.5 w-3.5" />
                {fmtMem(peakMemKb)}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
